'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { formatDiamond } from '@/utils/formatDiamond';
import { useGachaTicketShortage, isGachaTicketShortageError } from '@/hooks/useGachaTicketShortage';
import { useRouter } from 'next/navigation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Typography, ButtonBase, CircularProgress } from '@mui/material';
import { Icon } from '@/components/icons';
import IconPressable from '@/components/IconPressable';
import {
  TEMP_FG,
  TEMP_FG_PRIMARY,
  TEMP_FG_INVERSE,
  TEMP_FG_DIAMOND,
  TEMP_FG_LOWER,
  TEMP_BG_TRANSPARENT,
  TEMP_BG_INSET_NEUTRAL,
  TEMP_BORDER,
} from '@/constants/tempColors';
import {
  TEMP_TEXT_BOLD_L,
  TEMP_TEXT_BOLD_M,
  TEMP_TEXT_BOLD_XXL,
  TEMP_TEXT_MEDIUM_XXS,
  TEMP_TEXT_MEDIUM_XS,
  TEMP_TEXT_REGULAR_M,
  TEMP_TEXT_SEMIBOLD_XL,
  TEMP_TITLE_BOLD_M,
  TEMP_TITLE_EXTRABOLD_3XL,
} from '@/constants/tempTypo';
import { useTranslator } from '@/lang';
import { useAuthStore } from '@/stores/authStore';
import { usePortalStore } from '@/stores/portalStore';
import { useLoginModalStore } from '@/stores/loginModalStore';
import { useGachaStore } from '@/stores/gachaStore';
import { useToastStore } from '@/stores/toastStore';
import { useStackNavigation } from '@/hooks/useStackNavigation';
import { useAdultVerification } from '@/hooks/useAdultVerification';
import { useOverseasRestricted, useReviewerRestricted } from '@/hooks/useRestrictedMode';
import { useDiamondShortageModal } from '@/hooks/useDiamondShortageModal';
import { useDarkColors } from '@/hooks/useDarkColors';
import { getLocalizedText } from '@/utils/getLocalizedText';
import {
  getGachaInfo,
  getGachaPity,
  getGachaPreviews,
  getBalance,
  gachaPullOnce,
  gachaPullTen,
  type GachaPayWith,
} from '@/api/feedApi';
import type { GachaInfoOutput, GachaPityOutput } from '@/api/feedApi';
import { getAssetUrl } from '@/utils/assetUrl';
import { getErrorMessage } from '@/utils/errorMessages';
import { track } from '@/utils/analytics';
import { RIZZLEBOX_BANNERS_MYPAGE } from '@/app/features/RizzleBoxBanner/bannerAssets';
import SignupRewardInfo from "@/components/SignupRewardInfo/SignupRewardInfo";

// FIXME: 디자인 토큰 확정 후 제거
const TEMP_FG_PRIMARY_LOW = '#d5c4ff';

const PROBABILITY_GRADE_COLORS: Record<string, string> = {
  N: '#FFFFFF',
  R: '#4FC3F7',
  S: '#AB47BC',
  U: '#c084fc',
  L: '#ffe66a',
};

const GRADE_ORDER = ['N', 'R', 'S', 'U', 'L'];

export default function CardGachaPage() {
  const { push, pop, depth } = useStackNavigation();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { t, f, language } = useTranslator();
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const isAdultVerified = useAuthStore((s) => s.user?.isAdultVerified ?? false);
  // 심사용 테스트 계정: 성인인증 없이 뽑기 가능 (서버가 친밀도 아이템만 뽑히게 제한)
  const isTestAccount = useAuthStore((s) => s.user?.isTestAccount === true);
  const restricted = useReviewerRestricted(); // 심사 제한(test1239 · 해외 Apple 앱): 가챠 화면 없음(서버도 아이템 0 · 뽑기 403)
  const openModal = usePortalStore((s) => s.openModal);
  const openConfirm = usePortalStore((s) => s.openConfirm);
  const closeById = usePortalStore((s) => s.closeById);
  const openLoginModal = useLoginModalStore((s) => s.open);
  const setSingleResult = useGachaStore((s) => s.setSingleResult);
  const setMultiResult = useGachaStore((s) => s.setMultiResult);
  const storePityInfo = useGachaStore((s) => s.pityInfo);
  const setStorePityInfo = useGachaStore((s) => s.setPityInfo);
  const showToast = useToastStore((s) => s.show);
  const { verify: verifyAdult } = useAdultVerification();
  const { showDiamondShortage, isInsufficientBalanceError } = useDiamondShortageModal();
  const { offerDiamondPull } = useGachaTicketShortage();
  const c = useDarkColors();

  const handleClose = useCallback(() => {
    if (depth > 0) {
      pop();
    } else {
      router.replace('/');
    }
  }, [depth, pop, router]);

  const [gachaInfo, setGachaInfo] = useState<GachaInfoOutput | null>(null);
  const [pityInfo, setPityInfo] = useState<GachaPityOutput | null>(null);
  // 잔액은 로컬 스냅샷이 아니라 공용 라이브 쿼리로 읽는다(충전/소비 시 ['balance'] 무효화로 항상 최신).
  // 로컬 state로 들고 있으면 오버레이 충전 후에도 옛 값이 남아 "다이아 부족" 오모달이 뜬다(#제보).
  const { data: balanceData } = useQuery({
    queryKey: ['balance'],
    queryFn: () => getBalance(),
    enabled: isLoggedIn,
  });
  const balance = balanceData?.totalAmount ?? 0;
  const [gachaTickets, setGachaTickets] = useState<number>(0);
  const [isPulling, setIsPulling] = useState(false);

  useEffect(() => { track('rizzle_box_view'); }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [info, pity] = await Promise.all([
          getGachaInfo(),
          getGachaPity(),
        ]);
        setGachaInfo(info);
        setPityInfo(pity);
        setStorePityInfo({ current: pity.current, threshold: pity.threshold });
        setGachaTickets(info.gachaTickets ?? 0);
      } catch {
        try {
          const info = await getGachaInfo();
          setGachaInfo(info);
          setGachaTickets(info.gachaTickets ?? 0);
        } catch {}
      }
    };
    loadData();
  }, []);

  // 결과 화면(스택 위)의 "다시 뽑기"가 뽑기권을 쓰면 여기 수가 낡는다 → 돌아올 때(스택이 줄면) 다시 받기
  const refreshTickets = useCallback(() => {
    getGachaInfo().then((info) => {
      setGachaInfo(info);
      setGachaTickets(info.gachaTickets ?? 0);
    }).catch(() => {});
  }, []);
  const prevDepthRef = useRef(depth);
  useEffect(() => {
    if (depth < prevDepthRef.current) refreshTickets();
    prevDepthRef.current = depth;
  }, [depth, refreshTickets]);

  useEffect(() => {
    if (storePityInfo) {
      setPityInfo({
        current: storePityInfo.current,
        threshold: storePityInfo.threshold,
        remaining: storePityInfo.threshold - storePityInfo.current,
      });
    }
  }, [storePityInfo]);

  const showAdultVerificationModal = () => {
    let id = '';
    id = openModal({
      content: (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: '24px',
            pt: '36px',
            pb: '24px',
            gap: '24px',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <Box
              sx={{
                width: 56, height: 56, borderRadius: '50%',
                bgcolor: `${TEMP_FG_PRIMARY}14`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '8px',
              }}
            >
              <Icon name="icLock" size={28} color={TEMP_FG_PRIMARY} />
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.4, color: TEMP_FG, textAlign: 'center' }}>
              {t('gacha.adult_required')}
            </Typography>
            <Typography
              sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: TEMP_FG_LOWER, textAlign: 'center', whiteSpace: 'pre-line' }}
            >
              {t('gacha.adult_required_desc')}
            </Typography>
          </Box>
          <ButtonBase
            onClick={() => {
              closeById(id);
              verifyAdult();
            }}
            sx={{
              bgcolor: TEMP_FG_PRIMARY, borderRadius: '16px', py: '18px',
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG_INVERSE }}>
              {t('gacha.go_verify')}
            </Typography>
          </ButtonBase>
        </Box>
      ),
    });
  };

  const showPurchaseConfirm = (count: number, cost: number, onConfirm: () => void) => {
    const usingTicket = cost === 0;
    openConfirm({
      title: f(t('gacha.purchase_confirm'), [count]),
      subtitle: '',
      infoContent: (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          {usingTicket ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Box component="img" src="/svgs/ticket.svg" alt="" sx={{ width: 22, height: 22, objectFit: 'contain' }} />
              <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.4 }}>
                {f(t('gacha.ticket_use'), [count])}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Icon name="icDiamondFilled" size={24} color={TEMP_FG_DIAMOND} />
              <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.4 }}>
                {f(t('gacha.diamond_cost'), [formatDiamond(cost)])}
              </Typography>
            </Box>
          )}
          <Typography sx={{ fontSize: 15, fontWeight: 400, lineHeight: 1.4 }}>
            {usingTicket
              ? f(t('gacha.ticket_owned'), [gachaTickets])
              : f(t('gacha.diamond_balance'), [formatDiamond(balance)])}
          </Typography>
        </Box>
      ),
      cancelText: t('gacha.cancel'),
      confirmText: t('gacha.purchase'),
      onConfirm,
    });
  };

  const executeSingleGacha = useCallback(async (payWith: GachaPayWith) => {
    setIsPulling(true);
    try {
      const result = await gachaPullOnce(payWith);
      track('rizzle_box_open', { pull_type: 'single', used_ticket: result.usedTicket, cost: gachaInfo?.pullOnceCost ?? 200 });
      setSingleResult(result);
      setGachaTickets(result.remainingTickets ?? 0);
      const pity = { current: result.pity.current, threshold: result.pity.threshold };
      setPityInfo({ ...pity, remaining: pity.threshold - pity.current });
      setStorePityInfo(pity);
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['collection-detail'] });
      queryClient.invalidateQueries({ queryKey: ['balance'] });
      push('/card-gacha-result');
    } catch (err: any) {
      if (isInsufficientBalanceError(err)) {
        track('gacha_insufficient_diamond', { pull_type: 'single', required_cost: gachaInfo?.pullOnceCost ?? 200 });
        showDiamondShortage();
      } else if (isGachaTicketShortageError(err)) {
        // 화면엔 뽑기권이 있었는데 서버엔 없음 → 수를 고치고 다이아 구매로 잇는다
        refreshTickets();
        offerDiamondPull(1, gachaInfo?.pullOnceCost ?? 200, () => executeSingleGacha('DIAMOND'));
      } else {
        showToast('', getErrorMessage(err?.response?.data?.message, t));
      }
    } finally {
      setIsPulling(false);
    }
  }, [push, queryClient, gachaInfo, t, refreshTickets, offerDiamondPull]);

  const executeMultiGacha = useCallback(async (payWith: GachaPayWith) => {
    setIsPulling(true);
    try {
      const result = await gachaPullTen(payWith);
      track('rizzle_box_open', { pull_type: 'multi', used_tickets: result.usedTickets, cost: gachaInfo?.pullTenCost ?? 1800 });
      setMultiResult(result);
      setGachaTickets(result.remainingTickets ?? 0);
      const pity = { current: result.pity.current, threshold: result.pity.threshold };
      setPityInfo({ ...pity, remaining: pity.threshold - pity.current });
      setStorePityInfo(pity);
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['collection-detail'] });
      queryClient.invalidateQueries({ queryKey: ['balance'] });
      push('/card-gacha-result-multi');
    } catch (err: any) {
      if (isInsufficientBalanceError(err)) {
        track('gacha_insufficient_diamond', { pull_type: 'multi', required_cost: gachaInfo?.pullTenCost ?? 1800 });
        showDiamondShortage();
      } else if (isGachaTicketShortageError(err)) {
        refreshTickets();
        offerDiamondPull(10, gachaInfo?.pullTenCost ?? 1800, () => executeMultiGacha('DIAMOND'));
      } else {
        showToast('', getErrorMessage(err?.response?.data?.message, t));
      }
    } finally {
      setIsPulling(false);
    }
  }, [push, queryClient, gachaInfo, t, refreshTickets, offerDiamondPull]);

  const showSignupModal = useCallback(() => {
    openConfirm({
      title: t('profile.login_required_title'),
      subtitle: t('profile.signup_subtitle'),
      infoContent: <SignupRewardInfo />,
      cancelText: t('profile.cancel'),
      confirmText: t('profile.signup_cta'),
      confirmIcon: true,
      onConfirm: () => openLoginModal(),
    });
  }, [openConfirm, openLoginModal, t, c.fg]);

  const handleSingleGacha = useCallback(() => {
    if (!isLoggedIn) { showSignupModal(); return; }
    if (!isAdultVerified && !isTestAccount) { showAdultVerificationModal(); return; }
    if (isPulling) return;
    const cost = gachaInfo?.pullOnceCost ?? 200;
    const hasTicket = gachaTickets >= 1;
    if (!hasTicket && balance < cost) {
      track('gacha_insufficient_diamond', { pull_type: 'single', required_cost: cost, current_balance: balance });
      showDiamondShortage();
      return;
    }
    if (hasTicket) {
      showPurchaseConfirm(1, 0, () => executeSingleGacha('TICKET'));
    } else {
      showPurchaseConfirm(1, cost, () => executeSingleGacha('DIAMOND'));
    }
  }, [isLoggedIn, isAdultVerified, isTestAccount, isPulling, balance, gachaTickets, gachaInfo, executeSingleGacha, showSignupModal]);

  const handleMultiGacha = useCallback(() => {
    if (!isLoggedIn) { showSignupModal(); return; }
    if (!isAdultVerified && !isTestAccount) { showAdultVerificationModal(); return; }
    if (isPulling) return;
    const cost = gachaInfo?.pullTenCost ?? 1800;
    const hasTickets = gachaTickets >= 10;
    if (!hasTickets && balance < cost) {
      track('gacha_insufficient_diamond', { pull_type: 'multi', required_cost: cost, current_balance: balance });
      showDiamondShortage();
      return;
    }
    if (hasTickets) {
      showPurchaseConfirm(10, 0, () => executeMultiGacha('TICKET'));
    } else {
      showPurchaseConfirm(10, cost, () => executeMultiGacha('DIAMOND'));
    }
  }, [isLoggedIn, isAdultVerified, isTestAccount, isPulling, balance, gachaTickets, gachaInfo, executeMultiGacha, showSignupModal]);

  const pullOnceCost = gachaInfo?.pullOnceCost;
  const pullTenCost = gachaInfo?.pullTenCost;
  const pityCurrent = pityInfo ? pityInfo.threshold - pityInfo.remaining : 0;
  const pityThreshold = pityInfo?.threshold ?? 100;
  const pityProgress = pityThreshold > 0 ? (pityCurrent / pityThreshold) * 100 : 0;
  const isPityGuarantee = pityCurrent >= pityThreshold;
  const [showProbability, setShowProbability] = useState(false);

  if (restricted) return null;
  return (
    <Box
      sx={{
        position: 'relative',
        height: '100dvh',
        bgcolor: '#000',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 600,
        mx: 'auto',
      }}
    >
      {/* 배경 영상 (리즐박스 배너) */}
      <GachaBackground />
      {/* 어두운 오버레이 */}
      <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.55)', pointerEvents: 'none' }} />

      {/* Header */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          pt: 'var(--safe-area-top, 0px)',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 64,
            minHeight: 60,
            py: '14px',
          }}
        >
          <Box sx={{ pl: '20px', pr: '4px', py: '6px' }}>
            <IconPressable name="icArrowleft" size={24} color={TEMP_FG_INVERSE} onPress={handleClose} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', pr: '20px', pl: '4px', py: '6px' }}>
            <Icon name="icDiamondFilled" size={20} color={TEMP_FG_DIAMOND} />
            <Typography sx={{ ...TEMP_TEXT_BOLD_L, color: TEMP_FG_INVERSE }}>
              {balance.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* 타이틀 */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '2px',
          mt: '16px',
        }}
      >
        <Typography
          sx={{
            ...TEMP_TITLE_EXTRABOLD_3XL,
            color: TEMP_FG_INVERSE,
            textTransform: 'uppercase',
          }}
        >
          RIZZLE box
        </Typography>
        <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG_INVERSE }}>
          {t('gacha.description_2')}
        </Typography>
      </Box>

      {/* 빈 공간 (배경 영상 노출) */}
      <Box sx={{ flex: 1 }} />

      {/* 카드 캐러셀 (축소, 하단 배치) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 5,
          overflow: 'hidden',
          mb: '24px',
        }}
      >
        <GachaCarousel />
      </Box>

      {/* 피티 카운터 + 확률 정보 */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '11px',
          mb: '12px',
          px: '20px',
        }}
      >
        {/* 피티 텍스트 + 프로그레스 바 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', width: 300 }}>
          {isPityGuarantee ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                animation: 'pityGlow 1.5s ease-in-out infinite',
                '@keyframes pityGlow': {
                  '0%, 100%': { opacity: 0.85, filter: 'brightness(1)' },
                  '50%': { opacity: 1, filter: 'brightness(1.3)' },
                },
              }}
            >
              <Typography sx={{ fontSize: 16 }}>✨</Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 800,
                  background: 'linear-gradient(90deg, #ffe066, #ffb800, #ffe066)',
                  backgroundSize: '200% auto',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: 'pityShimmer 2s linear infinite',
                  '@keyframes pityShimmer': {
                    '0%': { backgroundPosition: '-200% center' },
                    '100%': { backgroundPosition: '200% center' },
                  },
                }}
              >
                {t('gacha.pity_next_guaranteed')}
              </Typography>
              <Typography sx={{ fontSize: 16 }}>✨</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: '2px', alignItems: 'baseline' }}>
              <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: TEMP_FG_PRIMARY_LOW }}>
                {f(t('gacha.pity_complete'), [pityThreshold])}
              </Typography>
              <Typography sx={{ ...TEMP_TEXT_MEDIUM_XS, color: TEMP_FG_INVERSE }}>
                {' '}{t('gacha.pity_legendary')}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', width: '100%' }}>
            {/* 프로그레스 바 */}
            <Box
              sx={{
                flex: 1,
                height: 8,
                bgcolor: TEMP_FG,
                borderRadius: '9999px',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${Math.min(pityProgress, 100)}%`,
                  minWidth: pityCurrent > 0 ? 8 : 0,
                  background: isPityGuarantee
                    ? 'linear-gradient(to right, #ffb800, #ffe066 50%, #ffb800)'
                    : `linear-gradient(to right, #b191ff, ${TEMP_FG_PRIMARY} 73.59%)`,
                  borderRadius: '9999px',
                  transition: 'width 0.3s ease',
                  ...(isPityGuarantee && {
                    animation: 'barGlow 1.5s ease-in-out infinite',
                    '@keyframes barGlow': {
                      '0%, 100%': { boxShadow: '0 0 4px rgba(255,184,0,0.4)' },
                      '50%': { boxShadow: '0 0 12px rgba(255,184,0,0.8)' },
                    },
                  }),
                }}
              />
            </Box>
            {/* 카운트 */}
            <Box sx={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
              <Typography sx={{ ...TEMP_TEXT_MEDIUM_XXS, color: isPityGuarantee ? '#ffe066' : TEMP_FG_INVERSE }}>
                {pityCurrent}
              </Typography>
              <Typography sx={{ ...TEMP_TEXT_MEDIUM_XXS, color: TEMP_FG_LOWER }}>
                / {pityThreshold}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* 등급별 아이템 확률 */}
        <ButtonBase
          onClick={() => setShowProbability(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            px: '12px',
            py: '8px',
          }}
        >
          <Typography sx={{ ...TEMP_TEXT_MEDIUM_XS, color: TEMP_FG_LOWER }}>
            {t('gacha.grade_probability')}
          </Typography>
          <Icon name="icChevronRight" size={16} color={TEMP_FG_LOWER} />
        </ButtonBase>
      </Box>

      {/* 뽑기권 보유 표시 */}
      {gachaTickets > 0 && (
        <Box
          sx={{
            position: 'relative',
            zIndex: 10,
            display: 'flex',
            justifyContent: 'center',
            mb: '8px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              bgcolor: 'rgba(255,255,255,0.12)',
              borderRadius: '20px',
              px: '14px',
              py: '6px',
            }}
          >
            <Box component="img" src="/svgs/ticket.svg" alt="" sx={{ width: 16, height: 16, objectFit: 'contain' }} />
            <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: TEMP_FG_INVERSE }}>
              {f(t('gacha.ticket_count'), [gachaTickets])}
            </Typography>
          </Box>
        </Box>
      )}

      {/* 뽑기 버튼들 */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          px: '20px',
          pb: 'calc(var(--safe-area-bottom, 0px) + 12px)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        {/* 1회 뽑기 */}
        <ButtonBase
          onClick={handleSingleGacha}
          disabled={isPulling}
          sx={{
            bgcolor: TEMP_FG,
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            height: 58,
            px: '10px',
            opacity: isPulling ? 0.5 : 1,
          }}
        >
          <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG_INVERSE }}>
            {t('gacha.single_draw')}
          </Typography>
          {gachaTickets >= 1 ? (
            <TicketPill amount={1} />
          ) : pullOnceCost != null ? (
            <DiamondPill amount={pullOnceCost} />
          ) : null}
        </ButtonBase>

        {/* 10회 뽑기 */}
        <ButtonBase
          onClick={handleMultiGacha}
          disabled={isPulling}
          sx={{
            bgcolor: TEMP_FG_PRIMARY,
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            height: 58,
            px: '10px',
            opacity: isPulling ? 0.5 : 1,
          }}
        >
          <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG_INVERSE }}>
            {t('gacha.multi_draw')}
          </Typography>
          {gachaTickets >= 10 ? (
            <TicketPill amount={10} />
          ) : pullTenCost != null ? (
            <DiamondPill amount={pullTenCost} />
          ) : null}
        </ButtonBase>
      </Box>

      {/* 로딩 오버레이 */}
      {isPulling && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1600, bgcolor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: TEMP_FG_PRIMARY }} size={60} />
        </Box>
      )}

      {/* 확률 정보 모달 */}
      {showProbability && (
        <ProbabilityModal
          probabilities={gachaInfo?.probabilities ?? []}
          language={language}
          t={t}
          onClose={() => setShowProbability(false)}
        />
      )}
    </Box>
  );
}

/** 뽑기권 pill 뱃지 */
function TicketPill({ amount }: { amount: number }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        bgcolor: TEMP_BG_TRANSPARENT,
        borderRadius: '40px',
        px: '8px',
        py: '4px',
      }}
    >
      <Box component="img" src="/svgs/ticket.svg" alt="" sx={{ width: 16, height: 16, objectFit: 'contain' }} />
      <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: TEMP_FG_INVERSE }}>
        {formatDiamond(amount)}
      </Typography>
    </Box>
  );
}

/** 다이아 가격 pill 뱃지 */
function DiamondPill({ amount }: { amount: number }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        bgcolor: TEMP_BG_TRANSPARENT,
        borderRadius: '40px',
        px: '8px',
        py: '4px',
      }}
    >
      <Icon name="icDiamondFilled" size={16} color={TEMP_FG_DIAMOND} />
      <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: TEMP_FG_INVERSE }}>
        {amount.toLocaleString()}
      </Typography>
    </Box>
  );
}

/** 무한 슬라이드 캐러셀 (requestAnimationFrame 기반) */
function GachaCarousel() {
  // 심사용 테스트 계정: 실제 미리보기 대신 기본 카드백 PNG로 캐러셀 렌더링
  // 해외 Apple 앱 유저는 캐러셀 자체를 렌더링하지 않음 (기존 동작 유지)
  const isTestAccount = useAuthStore((s) => s.user?.isTestAccount === true);
  const isOverseasRestricted = useOverseasRestricted();

  const CARD_SIZE = 88;
  const GAP = 12;
  const STEP = CARD_SIZE + GAP;

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef(0);
  const rafRef = useRef(0);
  const [centerIdx, setCenterIdx] = useState(0);
  type PreviewItem = { src: string; blur: boolean };
  const [previewItems, setPreviewItems] = useState<PreviewItem[]>([]);

  useEffect(() => {
    if (isTestAccount) {
      setPreviewItems(Array.from({ length: 8 }, () => ({ src: '/images/gacha_card_back.png', blur: false })));
      return;
    }
    if (isOverseasRestricted) return;
    getGachaPreviews()
      .then((data) => {
        const items = data.previews
          .map((p) => ({ src: getAssetUrl(p.src), blur: p.blur }))
          .filter((p) => p.src);
        // 운영 previews 는 655장(×3) — 앱(RN)은 이미지 대기열이 막혀 결과 카드까지 검게 떴다(2026-10-08). 웹 · 앱 같이 무작위 24장만
        for (let i = items.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [items[i], items[j]] = [items[j], items[i]];
        }
        if (items.length > 0) setPreviewItems(items.slice(0, 24));
      })
      .catch(() => {});
  }, [isTestAccount, isOverseasRestricted]);

  const oneSetLen = previewItems.length || 1;
  const oneSetWidth = oneSetLen * STEP;
  const images = useMemo(
    () => (previewItems.length > 0 ? [...previewItems, ...previewItems, ...previewItems] : []),
    [previewItems],
  );

  useEffect(() => {
    if (images.length === 0) return;
    scrollRef.current = oneSetWidth;
    const speed = 0.8;
    let lastCenter = -1;

    const animate = () => {
      scrollRef.current += speed;
      if (scrollRef.current >= oneSetWidth * 2) {
        scrollRef.current -= oneSetWidth;
      }
      if (containerRef.current) {
        containerRef.current.style.transform = `translateX(${-scrollRef.current}px)`;
      }
      const vw = typeof window !== 'undefined' ? window.innerWidth : 375;
      const centerPos = scrollRef.current + vw / 2;
      const idx = Math.round((centerPos - CARD_SIZE / 2) / STEP) % oneSetLen;
      const safeIdx = ((idx % oneSetLen) + oneSetLen) % oneSetLen;
      if (safeIdx !== lastCenter) {
        lastCenter = safeIdx;
        setCenterIdx(safeIdx);
      }
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [images.length, oneSetLen, oneSetWidth]);

  if (isOverseasRestricted) return null;

  if (images.length === 0) {
    return <Box sx={{ width: '100%', height: CARD_SIZE }} />;
  }

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          gap: `${GAP}px`,
          alignItems: 'center',
          willChange: 'transform',
        }}
      >
        {images.map((item, i) => {
          const isCenter = (i % oneSetLen) === centerIdx;
          return (
            <GachaCard
              key={i}
              size={CARD_SIZE}
              borderColor={isCenter ? TEMP_FG_INVERSE : 'transparent'}
              borderWidth={isCenter ? 2 : 1}
              imageSrc={item.src}
              blur={item.blur}
            />
          );
        })}
      </Box>
    </Box>
  );
}

/** 캐러셀 카드 (블러 이미지 + 자물쇠) */
function GachaCard({
  size,
  borderColor,
  borderWidth,
  imageSrc,
  blur,
}: {
  size: number;
  borderColor: string;
  borderWidth: number;
  imageSrc?: string;
  blur?: boolean;
}) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '12px',
        border: `${borderWidth}px solid ${borderColor}`,
        overflow: 'hidden',
        position: 'relative',
        flexShrink: 0,
        bgcolor: 'rgba(30,30,30,0.4)',
      }}
    >
      {imageSrc && (
        <Box
          component="img"
          src={imageSrc}
          alt=""
          draggable={false}
          sx={{
            position: 'absolute',
            ...(blur
              ? { inset: -6, width: 'calc(100% + 12px)', height: 'calc(100% + 12px)', filter: 'blur(6px)' }
              : { inset: 0, width: '100%', height: '100%' }),
            objectFit: 'cover',
            opacity: 0.55,
            pointerEvents: 'none',
          }}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Icon name="icLock" size={20} color="rgba(255,255,255,0.5)" />
      </Box>
    </Box>
  );
}

function GachaBackground() {
  const videos = useMemo(
    () => RIZZLEBOX_BANNERS_MYPAGE.filter((b) => b.type === 'video'),
    [],
  );
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * videos.length));

  const handleEnded = useCallback(() => {
    setIdx((prev) => (prev + 1) % videos.length);
  }, [videos.length]);

  // iOS(WKWebView·저전력 모드)는 autoplay 속성만으로 재생을 거부할 수 있다.
  // React가 muted를 HTML attribute로 내보내지 않는 것도 iOS autoplay 거부 사유가 되므로
  // muted를 직접 세팅하고 play()를 명시 호출, 거부되면 첫 터치에서 1회 재시도한다.
  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    if (!el) return;
    el.muted = true;
    el.defaultMuted = true;
    el.play().catch(() => {
      const retry = () => {
        if (el.isConnected) el.play().catch(() => {});
      };
      window.addEventListener('touchstart', retry, { once: true });
    });
  }, []);

  const current = videos[idx];
  if (!current) return null;

  return (
    <Box
      key={current.src}
      component="video"
      ref={attachVideo}
      src={current.src}
      autoPlay
      muted
      playsInline
      onEnded={handleEnded}
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        pointerEvents: 'none',
      }}
    />
  );
}

/** 확률 정보 모달 (등급별 토글 + 아이템 목록) */
function ProbabilityModal({
  probabilities,
  language,
  t,
  onClose,
}: {
  probabilities: GachaInfoOutput['probabilities'];
  language: string;
  t: (key: string) => string;
  onClose: () => void;
}) {
  const [openGrade, setOpenGrade] = useState<string | null>(null);
  const sorted = useMemo(
    () => [...probabilities].sort((a, b) => b.probability - a.probability),
    [probabilities],
  );

  return (
    <Box
      onClick={onClose}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1500,
        bgcolor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: '20px',
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: 'white',
          borderRadius: '20px',
          width: '100%',
          maxWidth: 380,
          maxHeight: '80dvh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Typography
          sx={{
            ...TEMP_TITLE_BOLD_M,
            color: TEMP_FG,
            textAlign: 'center',
            pt: '24px',
            pb: '16px',
            px: '24px',
            flexShrink: 0,
          }}
        >
          {t('gacha.grade_probability')}
        </Typography>

        <Box sx={{ flex: 1, overflowY: 'auto', px: '24px', pb: '8px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {sorted.map((p) => {
              const isOpen = openGrade === p.grade;
              const individualRate =
                p.itemCount > 0
                  ? (p.probability / p.itemCount).toFixed(4)
                  : '0';

              return (
                <Box key={p.grade}>
                  <ButtonBase
                    onClick={() => setOpenGrade(isOpen ? null : p.grade)}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: '12px',
                      px: '4px',
                      borderRadius: '8px',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '6px',
                          bgcolor: TEMP_FG,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: PROBABILITY_GRADE_COLORS[p.grade] || '#fff',
                          }}
                        >
                          {p.grade}
                        </Typography>
                      </Box>
                      <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: TEMP_FG }}>
                        {p.probability}%
                      </Typography>
                    </Box>
                    <Icon
                      name={isOpen ? 'icChevronUp' : 'icChevronDown'}
                      size={18}
                      color={TEMP_FG_LOWER}
                    />
                  </ButtonBase>

                  {isOpen && p.items && p.items.length > 0 && (
                    <Box
                      sx={{
                        bgcolor: TEMP_BG_INSET_NEUTRAL,
                        borderRadius: '12px',
                        px: '12px',
                        py: '8px',
                        mb: '4px',
                      }}
                    >
                      {p.items.map((item, idx) => {
                        const itemName = getLocalizedText(item.name, language);
                        return (
                          <Box key={item.id}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: '8px',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: TEMP_FG,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {itemName}
                                </Typography>
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: TEMP_FG_LOWER,
                                  flexShrink: 0,
                                  ml: '8px',
                                }}
                              >
                                {individualRate}%
                              </Typography>
                            </Box>
                            {idx < p.items.length - 1 && (
                              <Box sx={{ height: '0.5px', bgcolor: TEMP_BORDER }} />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ px: '24px', pb: '20px', pt: '8px', flexShrink: 0 }}>
          <ButtonBase
            onClick={onClose}
            sx={{
              width: '100%',
              bgcolor: TEMP_FG_PRIMARY,
              borderRadius: '12px',
              py: '14px',
            }}
          >
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG_INVERSE }}>
              {t('gacha.confirm')}
            </Typography>
          </ButtonBase>
        </Box>
      </Box>
    </Box>
  );
}
